import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Search, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { LoadingState, ErrorState } from "@/components/ui/data-display";
import { Pagination } from "@/components/ui/data-display/Pagination";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAllUnarchivedPoems, usePoets } from "@/hooks/data";
import { useAllPoets } from "@/hooks/data/useAllPoets";
import type { Database } from '@/types/supabase';
// Import our custom performance hooks
import useDebounce from "@/hooks/useDebounce";
import { 
  getAllPoetsWithPoems, 
  getAllPoetDetails,
  getPageForLetter,
  getDistinctInitialLetters
} from '@/services/poetService';

// Define types based on Supabase schema
type Poem = Database['public']['Views']['poem_details']['Row'];
type Poet = Database['public']['Tables']['poets']['Row'];

// Type for grouped poet data with poem counts and grouped poems by subject
type PoetWithPoemCount = {
  id?: string | null;  // Can be null from poem.poet_id
  name: string;        // Poet name from poem.poet_name
  displayName: string; // Format: "Last Name, First Name"
  lastName: string;    // Extracted last name for sorting
  poemCount: number;
  subjects: string[];
  poemsBySubject: Record<string, Poem[]>;
};

// Type for letter groups
type PoetsByLetter = {
  letter: string;
  poets: PoetWithPoemCount[];
};

// Type for viewing modes
type ViewMode = "poetsAndPoems" | "allPoets";

// Constants for pagination
const LETTERS_PER_PAGE = 6;
const POETS_PER_PAGE = 20;

const PoetIndex = () => {
  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>("poetsAndPoems");
  
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // Debug state
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    uniquePoetNames: [] as string[],
    processedPoets: 0
  });

  // State for poets and poems view
  const [filteredPoets, setFilteredPoets] = useState<PoetWithPoemCount[]>([]);
  const [alphabetLetters, setAlphabetLetters] = useState<string[]>([]);
  const [letterGroups, setLetterGroups] = useState<PoetsByLetter[]>([]);
  const [paginatedLetterGroups, setPaginatedLetterGroups] = useState<PoetsByLetter[]>([]);
  const [letterPageMap, setLetterPageMap] = useState<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // State for all poets view
  const [allPoetsPage, setAllPoetsPage] = useState(1);
  const [allPoetsPageSize, setAllPoetsPageSize] = useState(POETS_PER_PAGE);
  
  // State for distinct initial letters across all poets
  const [distinctInitialLetters, setDistinctInitialLetters] = useState<string[]>([]);
  const [initialLettersLoading, setInitialLettersLoading] = useState(true);
  const [initialLettersError, setInitialLettersError] = useState<Error | null>(null);
  // State for scrolling to a specific poet after page change
  const [scrollToPoetId, setScrollToPoetId] = useState<string | null>(null);
  
  const { toast } = useToast();
  
  // Fetch all poems using our custom hook with caching optimization
  const { 
    data: poems, 
    isLoading: isPoemsLoading, 
    error: poemsError,
    refetch: refetchPoems
  } = useAllUnarchivedPoems({
    useCache: true,
    cacheTTL: 10 * 60 * 1000 // 10 minutes cache
  });
  
  // Fetch all poets using our custom hook with caching optimization
  const { 
    data: poets, 
    isLoading: isPoetsLoading, 
    error: poetsError,
    refetch: refetchPoets,
  } = usePoets({
    initialParams: { page: 1, pageSize: 1000 }, // Get all poets to process
    useCache: true,
    cacheTTL: 5 * 60 * 1000 // 5 minutes cache for poets
  });
  
  // Fetch all poets with style points for the "All Poets" view
  const {
    data: allPoetsData, // This data *should* be sorted by lastName from the hook/backend, but isn't yet
    isLoading: isAllPoetsLoading,
    error: allPoetsError,
    refetch: refetchAllPoets,
    pagination: allPoetsPagination,
    setPageSize: updateAllPoetsPageSize,
    goToPage: goToAllPoetsPage,
    searchPoets,
    searchResults,
    isSearching,
    searchError,
    clearSearch,
    isSearchActive
  } = useAllPoets({
    initialParams: { page: allPoetsPage, pageSize: allPoetsPageSize },
    useCache: true,
    cacheTTL: 5 * 60 * 1000
  });
  
  // Decide which list of poets to display based on search state
  const poetsToDisplay = isSearchActive ? searchResults : allPoetsData;
  
  // Add a ref for the search input
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Update the useEffect for search to maintain focus
  useEffect(() => {
    if (viewMode === "allPoets") {
      if (debouncedSearchTerm) {
        console.log(`[PoetIndex useEffect] Triggering search for: "${debouncedSearchTerm}"`);
        searchPoets(debouncedSearchTerm)
          .finally(() => {
            // Restore focus to the search input after search completes
            // if (searchInputRef.current) { // <-- Temporarily comment out focus logic
            //   searchInputRef.current.focus();
            // }
          });
      } else {
        clearSearch();
      }
    }
  }, [debouncedSearchTerm, viewMode, searchPoets, clearSearch]);

  // Fetch distinct initial letters on mount
  useEffect(() => {
      setInitialLettersLoading(true);
      getDistinctInitialLetters()
          .then(letters => {
              console.log("Fetched distinct initial letters:", letters);
              setDistinctInitialLetters(letters);
              setInitialLettersError(null);
          })
          .catch(error => {
              console.error("Failed to fetch distinct initial letters:", error);
              setInitialLettersError(error);
              setDistinctInitialLetters([]); // Ensure it's an empty array on error
          })
          .finally(() => {
              setInitialLettersLoading(false);
          });
  }, []); // Empty dependency array ensures this runs only once on mount

  // Determine overall loading and error states
  const isLoading = 
    (viewMode === "poetsAndPoems" && (isPoemsLoading || isPoetsLoading)) || 
    (viewMode === "allPoets" && (isAllPoetsLoading || isSearching));
    
  const error = 
    (viewMode === "poetsAndPoems" && (poemsError || poetsError)) || 
    (viewMode === "allPoets" && (allPoetsError || searchError));

  // Helper function to format and extract poet name components
  const formatPoetName = useCallback((name: string): { displayName: string; lastName: string } => {
    // Handle edge cases like single names or initials
    const nameParts = name.trim().split(/\s+/);
    
    if (nameParts.length === 1) {
      // Single name (e.g., "Homer", "Sappho")
      return { 
        displayName: nameParts[0], 
        lastName: nameParts[0] 
      };
    } else if (nameParts.length === 2 && nameParts[0].length === 1 && nameParts[0].endsWith('.')) {
      // Initial and last name (e.g., "E. Dickinson")
      return { 
        displayName: `${nameParts[1]}, ${nameParts[0]}`, 
        lastName: nameParts[1] 
      };
    } else if (nameParts.length === 3 && 
               nameParts[0].length === 1 && nameParts[0].endsWith('.') && 
               nameParts[1].length === 1 && nameParts[1].endsWith('.')) {
      // Two initials and last name (e.g., "T. S. Eliot")
      return { 
        displayName: `${nameParts[2]}, ${nameParts[0]} ${nameParts[1]}`, 
        lastName: nameParts[2] 
      };
    } else {
      // Regular name format - assume last word is last name
      const lastName = nameParts.pop() || "";
      const firstName = nameParts.join(" ");
      return { 
        displayName: `${lastName}, ${firstName}`.trim(),
        lastName: lastName 
      };
    }
  }, []);

  // Memoized function to process poem data and extract poets
  const processPoetData = useCallback((poemsData: Poem[]): PoetWithPoemCount[] => {
    if (!poemsData.length) return [];
    
    // Log the first few poems to debug
    console.log('Sample poems data:', poemsData.slice(0, 3));
    
    // Get unique poet names from poems - using poet_name like the Book page does
    // Note: poemsData already excludes archived poems due to the usePoems hook configuration
    const uniquePoetNames = Array.from(
      new Set(poemsData
        .map(poem => poem.poet_name)
        .filter(Boolean))
    ) as string[];
    
    console.log('Unique poet names found:', uniquePoetNames);
    
    // Update debug info
    setDebugInfo({
      uniquePoetNames,
      processedPoets: uniquePoetNames.length
    });
    
    return uniquePoetNames.map(poetName => {
      // Get all poems by this poet - using poet_name like the Book page does
      // poemsData already excludes archived poems
      const poetPoems = poemsData.filter(poem => poem.poet_name === poetName);
      
      // Find the first poem for this poet to get a poet_id if available
      const firstPoem = poetPoems[0];
      const poetId = firstPoem?.poet_id;
      
      // Get unique subjects from poems
      const subjects = Array.from(new Set(
        poetPoems
          .map(poem => poem.subject)
          .filter(subject => subject !== null) as string[]
      )).sort();
      
      // Group poems by subject
      const poemsBySubject: Record<string, Poem[]> = {};
      subjects.forEach(subject => {
        // Filter poems for the current subject
        const subjectPoems = poetPoems.filter(poem => poem.subject === subject);
        
        // Sort by creation date descending (most recent first)
        // Assuming 'created_at' is available on the Poem type from the 'poem_details' view
        subjectPoems.sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA; // Descending order
        });
        
        // REMOVE .slice(0, 3) - Show all poems for the subject
        poemsBySubject[subject] = subjectPoems; 
      });
      
      // Format name for display
      const { displayName, lastName } = formatPoetName(poetName);
      
      return {
        id: poetId,  // Use ID if available from the poem's poet_id
        name: poetName,
        displayName,
        lastName,
        poemCount: poetPoems.length, // This now reflects only non-archived poems
        subjects: subjects,
        poemsBySubject: poemsBySubject
      };
    }).sort((a, b) => a.lastName.localeCompare(b.lastName)); // Sort by last name
  }, [formatPoetName]);

  // Process poems data with memoization
  useEffect(() => {
    if (isLoading || poems.length === 0 || viewMode !== 'poetsAndPoems') return;
    
    // Start processing with a small delay to not block the main thread
    const processingTimeout = setTimeout(() => {
      console.log(`Processing ${poems.length} poems data...`);
      
      // Process the data
      const poetsWithData = processPoetData(poems);
      console.log(`Found ${poetsWithData.length} poets with poems`);
      
      // Get first letters of last names for alphabet navigation
      const letters = Array.from(new Set(
        poetsWithData.map(poet => poet.lastName.charAt(0).toUpperCase())
      )).sort();
      
      console.log('Alphabet letters:', letters);
      setAlphabetLetters(letters);
      
      // Group poets by first letter for organization
      const grouped = letters.map(letter => ({
        letter,
        poets: poetsWithData.filter(poet => 
          poet.lastName.charAt(0).toUpperCase() === letter
        )
      }));
      
      console.log('Letter groups created:', grouped.length);
      
      // Set filtered poets and letter groups
      setFilteredPoets(poetsWithData);
      setLetterGroups(grouped);
      
      // Calculate total pages based on letter groups
      const calculatedTotalPages = Math.max(1, Math.ceil(grouped.length / LETTERS_PER_PAGE));
      console.log(`Total pages: ${calculatedTotalPages}`);
      setTotalPages(calculatedTotalPages);
      
      // Reset to page 1
      setCurrentPage(1);
      
      // Set the paginated letter groups for the first page
      const paginatedGroups = grouped.slice(0, LETTERS_PER_PAGE);
      setPaginatedLetterGroups(paginatedGroups);
      
      // Create initial letter page map
      const initialLetterPageMap: Record<string, number> = {};
      grouped.forEach((group, index) => {
        const pageNumber = Math.floor(index / LETTERS_PER_PAGE) + 1;
        initialLetterPageMap[group.letter] = pageNumber;
      });
      
      console.log('Initial letter page map:', initialLetterPageMap);
      setLetterPageMap(initialLetterPageMap);
    }, 0);
    
    return () => clearTimeout(processingTimeout);
  }, [poems, isLoading, processPoetData, LETTERS_PER_PAGE, viewMode]);

  // Update the paginated letter groups when page changes
  const updatePaginatedLetterGroups = useCallback((allGroups: PoetsByLetter[], page: number) => {
    console.log(`Updating paginated letter groups for page ${page}`, allGroups);
    
    const startIndex = (page - 1) * LETTERS_PER_PAGE;
    const endIndex = startIndex + LETTERS_PER_PAGE;
    const paginatedGroups = allGroups.slice(startIndex, endIndex);
    setPaginatedLetterGroups(paginatedGroups);
    
    // Create a mapping of letters to their pages
    const letterToPageMap: Record<string, number> = {};
    allGroups.forEach((group, index) => {
      const pageNumber = Math.floor(index / LETTERS_PER_PAGE) + 1;
      letterToPageMap[group.letter] = pageNumber;
    });
    
    console.log("Generated new letter page map:", letterToPageMap);
    setLetterPageMap(letterToPageMap);
  }, [LETTERS_PER_PAGE]);

  // Filter poets based on search term with debounce
  useEffect(() => {
    if (!debouncedSearchTerm.trim() || isLoading) {
      // If search is cleared, restore the full list
      if (poems.length && letterGroups.length > 0) {
        // Reprocess all poems to ensure we're starting fresh
        const allPoetsWithData = processPoetData(poems);
        setFilteredPoets(allPoetsWithData);
        
        // Recreate letter groups
        const allLetters = Array.from(new Set(
          allPoetsWithData.map(poet => poet.lastName.charAt(0).toUpperCase())
        )).sort();
        
        setAlphabetLetters(allLetters);
        
        const allGrouped = allLetters.map(letter => ({
          letter,
          poets: allPoetsWithData.filter(poet => 
            poet.lastName.charAt(0).toUpperCase() === letter
          )
        }));
        
        // Update letter groups
        setLetterGroups(allGrouped);
        
        // Update total pages
        const newTotalPages = Math.max(1, Math.ceil(allGrouped.length / LETTERS_PER_PAGE));
        setTotalPages(newTotalPages);
        
        // Reset to page 1
        setCurrentPage(1);
        
        // Update paginated groups for page 1
        const paginatedGroups = allGrouped.slice(0, LETTERS_PER_PAGE);
        setPaginatedLetterGroups(paginatedGroups);
        
        // Restore focus to the search input
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 0);
      }
      return;
    }
    
    const term = debouncedSearchTerm.toLowerCase();
    
    // Run the filtering in a non-blocking way
    const filteringTimeout = setTimeout(() => {
      // Filter poets by name directly from poems
      const poemsWithMatchingPoet = poems.filter(poem => 
        poem.poet_name && poem.poet_name.toLowerCase().includes(term)
      );
      
      // Only process if we have matches
      if (poemsWithMatchingPoet.length === 0) {
        setFilteredPoets([]);
        setLetterGroups([]);
        setPaginatedLetterGroups([]);
        setAlphabetLetters([]);
        setTotalPages(1);
        setCurrentPage(1);
        
        // Restore focus to the search input
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
        return;
      }
      
      // Process filtered poems
      const filtered = processPoetData(poemsWithMatchingPoet);
      setFilteredPoets(filtered);
      
      // Update letters and grouped poets for search results
      const letters = Array.from(new Set(
        filtered.map(poet => poet.lastName.charAt(0).toUpperCase())
      )).sort();
      
      setAlphabetLetters(letters);
      
      const grouped = letters.map(letter => ({
        letter,
        poets: filtered.filter(poet => 
          poet.lastName.charAt(0).toUpperCase() === letter
        )
      }));
      
      // Update letter groups
      setLetterGroups(grouped);
      
      // Reset to page 1
      setCurrentPage(1);
      
      // Update total pages
      const newTotalPages = Math.max(1, Math.ceil(grouped.length / LETTERS_PER_PAGE));
      setTotalPages(newTotalPages);
      
      // Update paginated groups for page 1
      const paginatedGroups = grouped.slice(0, LETTERS_PER_PAGE);
      setPaginatedLetterGroups(paginatedGroups);
      
      // Restore focus to the search input
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 0);
    
    return () => clearTimeout(filteringTimeout);
  }, [debouncedSearchTerm, poems, isLoading, processPoetData, LETTERS_PER_PAGE]);

  // Pagination handler - update to fix page navigation
  const handlePageChange = useCallback((newPage: number) => {
    console.log(`📄 Changing to page ${newPage}`);
    
    // Ensure newPage is within valid range
    if (newPage < 1 || newPage > totalPages) {
      console.log(`📄 Invalid page number: ${newPage}, valid range: 1-${totalPages}`);
      return;
    }
    
    // First update the current page state
    setCurrentPage(newPage);
    
    // Then explicitly calculate the paginated letter groups for the new page
    const startIndex = (newPage - 1) * LETTERS_PER_PAGE;
    const endIndex = startIndex + LETTERS_PER_PAGE;
    const newPaginatedGroups = letterGroups.slice(startIndex, endIndex);
    
    console.log(`📄 New paginated groups for page ${newPage}:`, newPaginatedGroups.map(g => g.letter));
    setPaginatedLetterGroups(newPaginatedGroups);
    
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [letterGroups, LETTERS_PER_PAGE, totalPages]);

  // Letter navigation function
  const scrollToLetter = useCallback((letter: string) => {
    // Add debug logging
    console.log(`💡 Attempting to scroll to letter: ${letter}`);
    console.log(`💡 Available letters:`, alphabetLetters);
    console.log(`💡 Current page:`, currentPage);
    console.log(`💡 Total letter groups:`, letterGroups.length);
    console.log(`💡 Letters per page:`, LETTERS_PER_PAGE);
    
    // First check if the letter exists in our current alphabet
    if (!alphabetLetters.includes(letter)) {
      console.log(`⚠️ Letter ${letter} not found in available letters.`);
      toast({
        title: "Letter not found",
        description: `There are no poets whose last name starts with "${letter}"`,
        variant: "destructive"
      });
      return;
    }
    
    // Find which page this letter should be on by checking letterPageMap
    const letterPage = letterPageMap[letter];
    console.log(`💡 Letter ${letter} should be on page ${letterPage}`);
    
    if (!letterPage) {
      console.log(`⚠️ Letter ${letter} not found in letter page map`);
      return;
    }
    
    if (letterPage !== currentPage) {
      // Change to the correct page first
      console.log(`💡 Changing from page ${currentPage} to page ${letterPage}`);
      handlePageChange(letterPage);
      
      // Wait for page to update before scrolling
      setTimeout(() => {
        const letterElement = document.getElementById(`letter-${letter}`);
        console.log(`💡 After page change, letter element found:`, !!letterElement);
        
        if (letterElement) {
          letterElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          console.log(`✅ Scrolled to letter ${letter}`);
        } else {
          console.log(`❌ Failed to find letter element after page change`);
          
          // Try one more time with longer delay
          setTimeout(() => {
            const retryElement = document.getElementById(`letter-${letter}`);
            if (retryElement) {
              retryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              console.log(`✅ Scrolled to letter ${letter} on second attempt`);
            }
          }, 300);
        }
      }, 100);
    } else {
      // Letter is on current page, just scroll to it
      const letterElement = document.getElementById(`letter-${letter}`);
      console.log(`💡 Letter element on current page:`, !!letterElement);
      if (letterElement) {
        letterElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        console.log(`✅ Scrolled to letter ${letter} on current page`);
      } else {
        console.log(`❌ Failed to find letter element on current page`);
      }
    }
  }, [currentPage, alphabetLetters, letterPageMap, toast, handlePageChange]);

  // Ensure letter page mappings are always current
  useEffect(() => {
    // If we have letter groups, update letter page map
    if (letterGroups.length > 0) {
      const newLetterToPageMap: Record<string, number> = {};
      
      // Directly map each letter to its page number
      letterGroups.forEach((group, index) => {
        const pageNumber = Math.floor(index / LETTERS_PER_PAGE) + 1;
        newLetterToPageMap[group.letter] = pageNumber;
      });
      
      // Debug output
      console.log("Updated letter page map:", newLetterToPageMap);
      setLetterPageMap(newLetterToPageMap);
    }
  }, [letterGroups, LETTERS_PER_PAGE]);

  // Add additional debug function to check letter groups when mounted
  useEffect(() => {
    console.log("Current letter page map:", letterPageMap);
    console.log("Available letters:", alphabetLetters);
    console.log("Letter groups:", letterGroups.map(g => g.letter));
    console.log("Paginated letter groups:", paginatedLetterGroups.map(g => g.letter));
  }, [letterPageMap, alphabetLetters, letterGroups, paginatedLetterGroups]);

  // Make sure paginated letter groups are refreshed when letterGroups changes
  useEffect(() => {
    // When letterGroups changes (e.g., after filtering), update paginated groups
    console.log('📊 Letter groups changed, updating paginated groups', { 
      letterGroups: letterGroups.length, 
      currentPage 
    });
    
    // Only proceed if letterGroups has content
    if (letterGroups.length === 0) return;
    
    // Calculate the maximum valid page
    const maxPage = Math.max(1, Math.ceil(letterGroups.length / LETTERS_PER_PAGE));
    
    // Adjust current page if needed
    if (currentPage > maxPage) {
      console.log(`📊 Adjusting current page from ${currentPage} to ${maxPage} due to group changes`);
      setCurrentPage(maxPage);
    }
    
    // Calculate the start and end index for the current page
    const startIndex = (currentPage <= maxPage ? currentPage - 1 : 0) * LETTERS_PER_PAGE;
    const endIndex = startIndex + LETTERS_PER_PAGE;
    const paginatedGroups = letterGroups.slice(startIndex, endIndex);
    
    console.log(`📊 Setting paginated groups for page ${currentPage}:`, 
      paginatedGroups.map(g => g.letter)
    );
    
    // Update the paginated groups
    setPaginatedLetterGroups(paginatedGroups);
    
    // Also update totalPages if needed
    const newTotalPages = Math.max(1, Math.ceil(letterGroups.length / LETTERS_PER_PAGE));
    if (newTotalPages !== totalPages) {
      setTotalPages(newTotalPages);
    }
    
  }, [letterGroups, currentPage, LETTERS_PER_PAGE, totalPages]);

  // Handle page change for "Poets and Poems" view
  const handlePageChangePoetsAndPoems = useCallback((page: number) => {
    // ... existing code ...
  }, [letterGroups]);
  
  // Handle page change for "All Poets" view
  const handleAllPoetsPageChange = useCallback((page: number) => {
    setAllPoetsPage(page);
    goToAllPoetsPage(page);
  }, [goToAllPoetsPage]);
  
  // Handle page size change for "All Poets" view
  const handleAllPoetsPageSizeChange = useCallback((pageSize: number) => {
    setAllPoetsPageSize(pageSize);
    setAllPoetsPage(1); // Reset to first page
    updateAllPoetsPageSize(pageSize);
  }, [updateAllPoetsPageSize]);
  
  // Handle view mode change
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    setSearchTerm("");
    if (mode === "allPoets") {
      console.log('Switching to All Poets view, refreshing data');
      clearSearch();
      // Ensure we fetch *all* poets when switching to this view if not already fetched
      // refetchAllPoets should use the currently set parameters (like pageSize)
      refetchAllPoets(); // Call without arguments
    }
    // Restore focus to the search input after view change
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 0);
  }, [refetchAllPoets, clearSearch]);

  // Jump to letter function for All Poets view
  const jumpToLetterAllPoets = useCallback(async (letter: string) => {
    console.log(`💡 Attempting to jump to letter ${letter} in All Poets view`);
    
    // Basic validation on the letter itself
    if (!letter || letter.length !== 1 || !/^[A-Z]$/.test(letter)) {
        console.error(`Invalid letter provided: ${letter}`);
        return;
    }

    // We don't strictly need to check allPoetsLetters includes(letter) here,
    // as the backend service will handle finding the correct page regardless.
    // We trust the backend to know all letters.

    try {
      console.log(`💡 Calling getPageForLetter with: letter=${letter}, pageSize=${allPoetsPageSize}, sortBy=lastName`);
      // Call the service function to get the page number AND first poet ID
      const { targetPage, firstPoetId } = await getPageForLetter(letter, allPoetsPageSize, 'lastName');

      console.log(`💡 Received target page ${targetPage}, firstPoetId ${firstPoetId} from backend`);

      // Go to the target page using the existing handler
      if (targetPage > 0) {
        handleAllPoetsPageChange(targetPage);
        
        // Set the ID of the poet to scroll to
        if (firstPoetId) {
            console.log(`💡 Setting scrollToPoetId to: ${firstPoetId}`);
            setScrollToPoetId(firstPoetId); 
        } else {
            // If no ID came back, just scroll to top as a fallback
            console.log("⚠️ No firstPoetId received, scrolling to top.");
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setScrollToPoetId(null); // Ensure state is cleared
        }

        // --- REMOVE OLD SCROLLING LOGIC --- 
        // setTimeout(() => {
        //     window.scrollTo({ top: 0, behavior: 'smooth' });
        //      console.log("[TEMP] Scrolled to top after page change for letter:", letter);
        // }, 150); 
        // setScrollToPoetId(SOME_LOGIC_TO_GET_FIRST_POET_ID_OF_TARGET_PAGE);

      } else {
        // This might happen if the backend logic fails or no poet starts with that letter
        console.warn(`⚠️ Backend returned non-positive target page ${targetPage} for letter ${letter}`);
        toast({ title: "Not Found", description: `Could not find page for letter "${letter}".`, variant: "default" });
      }
    } catch (error) {
      console.error(`Error jumping to letter ${letter}:`, error);
      toast({
        title: "Navigation Error",
        description: `Failed to navigate to letter ${letter}. Please try again.`, 
        variant: "destructive"
      });
    }

  }, [allPoetsPageSize, handleAllPoetsPageChange, toast, setScrollToPoetId]); // Add setScrollToPoetId to dependency array

  // Effect to scroll to the target poet when data changes
  useEffect(() => {
    // console.log(`[Scroll Effect] Running. scrollToPoetId: ${scrollToPoetId}`); // Removed debug log
    // Only proceed if we have a target ID and poets are actually loaded/displayed
    if (scrollToPoetId && poetsToDisplay.length > 0) { 
        // console.log(`[Scroll Effect] Condition met. Checking for poet ${scrollToPoetId} in ${poetsToDisplay.length} poets.`); // Removed debug log
        // Check if the target poet is actually in the current data set being displayed
        const targetPoetExists = poetsToDisplay.some(p => p.id === scrollToPoetId);
        // console.log(`[Scroll Effect] Target poet exists in poetsToDisplay: ${targetPoetExists}`); // Removed debug log
        
        if (targetPoetExists) {
            // Attempt to find the element
            const element = document.getElementById(`poet-list-item-${scrollToPoetId}`);
            // console.log(`[Scroll Effect] getElementById result for poet-list-item-${scrollToPoetId}:`, element); // Removed debug log
            
            if (element) {
                // Element found, scroll and reset
                // console.log(`[Scroll Effect] Element found. Calling scrollIntoView...`); // Removed debug log
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start' // Align to the top of the viewport
                });
                // Reset ONLY after successful scroll attempt
                setScrollToPoetId(null);
                // console.log(`[Scroll Effect] Scroll target reset to null.`); // Removed debug log
            } else {
                // Element not found *yet*, might appear on next render cycle.
                // Do nothing here, the effect will re-run if poetsToDisplay changes.
                console.log(`[Scroll Effect] Element with ID poet-list-item-${scrollToPoetId} not found yet. Waiting for next render.`); // Keep this one - indicates potential issue
            }
        } else {
            // Target poet is not in the *current* list. 
            // Do nothing, wait for poetsToDisplay to potentially update and re-run the effect.
            // console.log(`[Scroll Effect] Target poet ID ${scrollToPoetId} not found in current poetsToDisplay. Waiting...`); // Removed debug log
        }
    } 
    // If scrollToPoetId is set, but poetsToDisplay is empty (e.g., during loading), 
    // this effect does nothing and will run again when poetsToDisplay populates.
    
    // Dependencies: scroll target ID and the list of poets currently displayed
  }, [scrollToPoetId, poetsToDisplay, setScrollToPoetId]);

  // Show loading state
  if (isLoading) {
    return <LoadingState text="Loading poets..." />;
  }

  // Show error state
  if (error) {
    return (
      <ErrorState 
        error={error} 
        onRetry={() => {
          if (viewMode === 'poetsAndPoems') {
              refetchPoems(); refetchPoets();
          } else {
              refetchAllPoets();
          }
        }}
        title="Failed to load poets"
      />
    );
  }

  // Function to render style points safely 
  const renderStylePoints = (stylePoints: any) => {
    if (Array.isArray(stylePoints)) {
      return stylePoints.map((point, index) => (
        <li key={index} className="text-muted-foreground">
          {String(point)}
        </li>
      ));
    } else if (stylePoints && typeof stylePoints === 'object') {
      return Object.entries(stylePoints).map(([key, value], index) => (
        <li key={index} className="text-muted-foreground">
          {`${key}: ${value}`}
        </li>
      ));
    } else if (stylePoints) {
      return (
        <li className="text-muted-foreground">
          {String(stylePoints)}
        </li>
      );
    } else {
      return (
        <li className="text-muted-foreground italic">
          No style information available
        </li>
      );
    }
  };

  // Log state right before render
  console.log("[PoetIndex Render] View Mode:", viewMode);
  console.log("[PoetIndex Render] Distinct Initial Letters:", distinctInitialLetters);
  console.log("[PoetIndex Render] Initial Letters Loading:", initialLettersLoading);
  console.log("[PoetIndex Render] Initial Letters Error:", initialLettersError);
  console.log("[PoetIndex Render] Is Searching:", isSearching);
  console.log("[PoetIndex Render] Search Term:", searchTerm);

  // Log the poets being sent to the render function
  // console.log("[PoetIndex Render] poetsToDisplay (length):", poetsToDisplay.length, poetsToDisplay.slice(0, 5)); // Removed debug log

  return (
    <div className="min-h-screen pt-16">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="font-poetry text-4xl font-semibold mb-8">Poet Index</h1>
        
        {/* View mode toggle */}
        <div className="mb-6">
          <Tabs 
            value={viewMode} 
            onValueChange={(value) => handleViewModeChange(value as ViewMode)}
            className="mx-auto w-full max-w-md"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="poetsAndPoems">Poets and Poems</TabsTrigger>
              <TabsTrigger value="allPoets">All Poets</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      
        {/* Search and filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-grow">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search poets by name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
              data-testid="poet-search-input"
            />
          </div>
        </div>
        
        {/* Debug info - hidden by default but preserved in code */}
        {viewMode === "poetsAndPoems" && showDebugInfo && (
          <div className="debug-info bg-gray-100 p-3 rounded-lg border shadow-sm mb-4 text-xs">
            <h3 className="font-bold">Debug Info:</h3>
            <p>Total poems: {poems.length}</p>
            <p>Unique poet names found in poems: {debugInfo.uniquePoetNames.length}</p>
            <p>Processed poets: {debugInfo.processedPoets}</p>
            <p>Displayed poets: {filteredPoets.length}</p>
            <p>Letter groups: {letterGroups.length}</p>
            <div className="mt-2">
              <strong>Poets in Book but missing from Index:</strong>
              <ul className="list-disc pl-4">
                {debugInfo.uniquePoetNames
                  .filter(name => !filteredPoets.some(poet => poet.name === name))
                  .map(name => (
                    <li key={name}>{name}</li>
                  ))
                }
              </ul>
            </div>
          </div>
        )}
        
        {/* "Poets and Poems" View */}
        {viewMode === "poetsAndPoems" && (
          <>
            {/* Alphabet navigation - horizontal scrollable list */}
            <div className="bg-card p-3 rounded-lg border shadow-sm mb-6 overflow-x-auto" data-testid="alphabet-navigation">
              <div className="text-center font-semibold mb-2">Jump to</div>
              <div className="flex justify-center gap-1 min-w-max">
                {/* Display all alphabet letters, disable those without poets */}
                {Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ").map((letter) => {
                  const letterHasPoets = alphabetLetters.includes(letter);
                  return (
                    <Button
                      key={letter}
                      variant={letterHasPoets ? "ghost" : "outline"}
                      size="sm"
                      onClick={() => letterHasPoets && scrollToLetter(letter)}
                      className={`w-8 h-8 p-0 ${!letterHasPoets ? 'opacity-40 cursor-not-allowed' : ''}`}
                      disabled={!letterHasPoets}
                      title={letterHasPoets ? `Jump to poets with names beginning with ${letter}` : `No poets with names beginning with ${letter}`}
                    >
                      {letter}
                    </Button>
                  );
                })}
              </div>
            </div>
            
            {/* Pagination controls - top */}
            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={LETTERS_PER_PAGE}
                  onPageChange={handlePageChange}
                  data-testid="poet-pagination"
                  showPageSizeSelector={false}
                />
              </div>
            )}
            
            {/* Main content - poet listings by letter */}
            <div className="bg-card rounded-lg border shadow-sm p-6">
              {/* Letter groups layout */}
              {paginatedLetterGroups.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
                  {paginatedLetterGroups.map((letterGroup, groupIndex) => (
                    <div key={letterGroup.letter} id={`letter-${letterGroup.letter}`} className="mb-4">
                      <h2 className="text-xl font-bold mb-4 sticky top-0 bg-background/95 backdrop-blur-sm py-2 z-10 border-b">
                        {letterGroup.letter}
                      </h2>
                      
                      <ul className="space-y-4">
                        {letterGroup.poets
                          .filter(poet => poet.poemCount > 0) // Only show poets with poems
                          .map(poet => (
                            <li key={`${poet.name}-${poet.id || 'no-id'}`} className="poet-card p-4 rounded-lg border hover:border-primary transition-colors">
                              <h3 className="text-lg font-medium mb-2">
                                {(() => {
                                  // Find poem with poetry foundation URL
                                  const poetFoundationUrl = poet.id ? 
                                    poems.find(p => p.poet_id === poet.id)?.poetry_foundation_url : 
                                    undefined;
                                    
                                  return poetFoundationUrl ? (
                                    <a 
                                      href={poetFoundationUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="hover:text-primary hover:underline transition-colors"
                                    >
                                      {poet.displayName}
                                    </a>
                                  ) : (
                                    poet.displayName
                                  );
                                })()}
                              </h3>
                              <div className="text-sm text-muted-foreground mb-3">
                                {/* Display poem count - only showing non-archived poems due to filtering in usePoems hook */}
                                {poet.poemCount} {poet.poemCount === 1 ? 'poem' : 'poems'} inspired by the style of {poet.name}
                              </div>
                              
                              <div className="pl-2 space-y-3">
                                {poet.subjects.map(subject => (
                                  <div key={subject} className="subject-group">
                                    <div className="font-medium text-sm text-muted-foreground mb-1">
                                      {subject}
                                    </div>
                                    <ul className="pl-4 space-y-1">
                                      {poet.poemsBySubject[subject].map(poem => (
                                        <li key={poem.id} className="text-sm hover:text-primary">
                                          <Link 
                                            to={`/book?poet=${encodeURIComponent(poem.poet_name || poet.name)}&poemId=${poem.id}&subject=${encodeURIComponent(subject)}`}
                                            className="hover:underline transition-colors"
                                            data-testid={`poem-link-${poem.id}`}
                                          >
                                            {poem.is_protected && <Shield className="inline-block h-3.5 w-3.5 mr-1.5 text-blue-600 align-[-2px]" aria-label="Protected Poem"/>}
                                            {poem.title}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </li>
                          ))}
                      </ul>
                      
                      {groupIndex < paginatedLetterGroups.length - 1 && 
                        <Separator className="mt-6 lg:hidden" />}
                    </div>
                  ))}
                </div>
              ) : searchTerm ? (
                <div className="text-center py-8">
                  <p className="text-lg mb-4">No poets matching "{searchTerm}" were found.</p>
                  <Button 
                    onClick={() => setSearchTerm("")}
                    variant="outline"
                  >
                    Clear Search
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-lg">No poets with poems are available.</p>
                </div>
              )}
            </div>
            
            {/* Pagination controls - bottom */}
            {totalPages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={LETTERS_PER_PAGE}
                  onPageChange={handlePageChange}
                  data-testid="poet-pagination"
                  showPageSizeSelector={false}
                />
              </div>
            )}
          </>
        )}
        
        {/* "All Poets" View */}
        {viewMode === "allPoets" && (
          <>
            {/* Alphabet navigation for All Poets */}
            <div className="bg-card p-3 rounded-lg border shadow-sm mb-6 overflow-x-auto" data-testid="all-poets-alphabet-navigation">
              <div className="text-center font-semibold mb-2">Jump to</div>
              <div className="flex justify-center gap-1 min-w-max">
                {Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ").map((letter) => {
                  const letterHasPoets = distinctInitialLetters.includes(letter);
                  return (
                    <Button
                      key={letter}
                      variant={letterHasPoets ? "ghost" : "outline"}
                      size="sm"
                      onClick={() => letterHasPoets && jumpToLetterAllPoets(letter)}
                      className={`w-8 h-8 p-0 ${!letterHasPoets ? 'opacity-40 cursor-not-allowed' : ''}`}
                      disabled={isSearchActive || !letterHasPoets}
                      title={letterHasPoets ? `Jump to poets with names beginning with ${letter}` : `No poets with names beginning with ${letter}`}
                    >
                      {letter}
                    </Button>
                  );
                })}
              </div>
            </div>
            
            {/* Only show pagination when not searching */}
            {allPoetsPagination && allPoetsPagination.totalPages > 1 && searchResults.length === 0 && (
              <div className="mb-6">
                <Pagination
                  currentPage={allPoetsPagination.currentPage}
                  totalPages={allPoetsPagination.totalPages}
                  pageSize={allPoetsPagination.pageSize}
                  pageSizeOptions={[10, 20, 50, 100]}
                  onPageChange={handleAllPoetsPageChange}
                  onPageSizeChange={handleAllPoetsPageSizeChange}
                  data-testid="all-poets-pagination"
                />
              </div>
            )}
            
            {/* List of all poets with style points */}
            <div className="bg-card rounded-lg border shadow-sm p-6">
              {/* Conditionally render search results or paginated data */}
              {poetsToDisplay.length > 0 ? (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {poetsToDisplay.map((poet) => {
                    if (!poet || !poet.id) return null; 
                    const { displayName } = formatPoetName(poet.name || "Unknown Poet");
                    return (
                      <li 
                        key={poet.id} 
                        id={`poet-list-item-${poet.id}`} // Ensure ID is added here
                        className="poet-card p-4 rounded-lg border hover:border-primary transition-colors scroll-mt-24"
                      >
                        <h3 className="text-lg font-medium mb-2">
                          {poet.poetry_foundation_url ? (
                            <a 
                              href={poet.poetry_foundation_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-primary hover:underline transition-colors"
                            >
                              {displayName}
                            </a>
                          ) : (
                            displayName
                          )}
                        </h3>
                        <div className="mt-4">
                          <h4 className="text-sm font-semibold mb-2">Style Points</h4>
                          <ul className="pl-2 space-y-1 text-sm">
                            {renderStylePoints(poet.style_points)}
                          </ul>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-center py-8">
                  {/* Adjust message based on whether search was active */}
                  <p className="text-lg">{isSearchActive ? "No search results found." : "No poets are available."}</p>
                </div>
              )}
            </div>
            
            {/* Conditionally render pagination only when search is NOT active */}
            {!isSearchActive && allPoetsPagination && allPoetsPagination.totalPages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={allPoetsPagination.currentPage}
                  totalPages={allPoetsPagination.totalPages}
                  pageSize={allPoetsPagination.pageSize}
                  pageSizeOptions={[10, 20, 50, 100]}
                  onPageChange={handleAllPoetsPageChange}
                  onPageSizeChange={handleAllPoetsPageSizeChange}
                  data-testid="all-poets-pagination"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PoetIndex; 